/**
 * @module getPostUrlPrefix
 *
 * Funções client-safe para construção de caminhos de posts.
 * Lê NEXT_PUBLIC_POST_URL_PREFIX (env var) — sem dependências de servidor.
 *
 * Para o prefixo vindo do CMS (Server Components / Route Handlers),
 * use `getPostUrlPrefixServer` de `@/utilities/getPostUrlPrefixServer`.
 */

import { safeDecodeURIComponent } from '@/utilities/safeDecodeURIComponent'

/**
 * Decodificação segura de segmentos de rota — evita URIError com `%` mal formados.
 * Delega a `safeDecodeURIComponent` (fonte única); mantém o contrato próprio de devolver
 * string vazia para entrada que não é string.
 */
export function safeDecodePathSegment(segment: string): string {
  if (typeof segment !== 'string') return ''
  return safeDecodeURIComponent(segment)
}

/**
 * Normaliza um segmento de URL (prefixo ou slug de rota) para comparação e hrefs.
 * Evita 404 por diferença só de maiúsculas ou barras a mais.
 */
export function normalizePostUrlPrefixSegment(segment: string): string {
  const s = safeDecodePathSegment(segment).trim().replace(/^\/+|\/+$/g, '')
  return (s || 'posts').toLowerCase()
}

/** Retorna o prefixo da env var — seguro em client e server. */
export function getPostUrlPrefix(): string {
  return normalizePostUrlPrefixSegment(process.env.NEXT_PUBLIC_POST_URL_PREFIX ?? 'posts')
}

/**
 * Constrói o caminho público de um post individual.
 * @param slug   — slug do post
 * @param prefix — prefixo explícito (server-side); omitir usa env var (client-side)
 * @example buildPostPath('seguro-alta', 'noticias') → '/noticias/seguro-alta'
 */
export function buildPostPath(slug: string, prefix = getPostUrlPrefix()): string {
  const p = normalizePostUrlPrefixSegment(prefix)
  return `/${p}/${slug}`
}

/**
 * Retorna o caminho do arquivo de posts.
 * @example buildPostsArchivePath('noticias') → '/noticias'
 */
export function buildPostsArchivePath(prefix = getPostUrlPrefix()): string {
  return `/${normalizePostUrlPrefixSegment(prefix)}`
}

/**
 * Constrói o caminho de uma página paginada do arquivo.
 *
 * SEO-2 (auditoria SEO 14/09): a página 1 é o PRÓPRIO arquivo, como a categoria e a tag já fazem.
 * Antes devolvia `/posts/page/1` — cópia de `/posts` com canonical próprio, linkada pela paginação
 * de `/posts` e de `/posts/page/2`. A rota `/posts/page/1` agora responde 308 para o arquivo.
 * @example buildPostsPagePath(1, 'noticias') → '/noticias'
 * @example buildPostsPagePath(2, 'noticias') → '/noticias/page/2'
 */
export function buildPostsPagePath(pageNumber: number, prefix = getPostUrlPrefix()): string {
  if (pageNumber <= 1) return buildPostsArchivePath(prefix)
  const p = normalizePostUrlPrefixSegment(prefix)
  return `/${p}/page/${pageNumber}`
}
